import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Plus, ListChecks, FloppyDisk, DownloadSimple, SealCheck } from "@phosphor-icons/react";
import { BoxCard, blankBox, cbm, norm, num } from "@/components/PackingListDialog";
import { fmtDateTime } from "@/components/common";

const Field = ({ label, children }) => <label className="block"><span className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</span>{children}</label>;
const download = (blob, name) => { const url = URL.createObjectURL(blob); const a = document.createElement("a"); a.href = url; a.download = name; a.click(); URL.revokeObjectURL(url); };

export const PackingStatusChip = ({ status }) => {
  if (!status || status === "none") return null;
  const issued = status === "issued";
  return <span className={`inline-flex items-center gap-1 rounded-sm px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${issued ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`} data-testid="packing-status-chip">{issued ? <SealCheck size={11} weight="fill" /> : <FloppyDisk size={11} />} {issued ? "PL issued" : "PL draft"}</span>;
};

export const ProformaPackingDialog = ({ proforma: pf, open, onOpenChange, onChanged }) => {
  const [boxes, setBoxes] = useState([]);
  const [items, setItems] = useState([]);
  const [shipper, setShipper] = useState("");
  const [consignee, setConsignee] = useState("");
  const [notes, setNotes] = useState("");
  const [meta, setMeta] = useState({ status: "none" });
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);

  useEffect(() => {
    if (!open || !pf) return;
    api.get(`/quote-proformas/${pf.id}/packing-list/prefill`).then(({ data }) => {
      setBoxes(data.boxes.map((b) => ({ ...b, items: b.items || [] }))); setItems(data.items); setShipper(data.shipper_text); setConsignee(data.consignee_text); setNotes(data.notes || "");
      setMeta({ status: data.status, issued_at: data.issued_at, issued_by: data.issued_by, saved_at: data.saved_at });
    }).catch((e) => toast.error(e?.response?.data?.detail || "Could not load packing data"));
  }, [open, pf?.id]);

  const totals = useMemo(() => ({
    weight: boxes.reduce((a, b) => a + num(b.weight_kg), 0), cbm: boxes.reduce((a, b) => a + cbm(b), 0),
    qty: boxes.reduce((a, b) => a + b.items.reduce((x, it) => x + num(it.quantity), 0), 0),
  }), [boxes]);
  const remaining = useMemo(() => {
    const r = {};
    items.forEach((c) => { r[norm(c.name)] = (r[norm(c.name)] || 0) + num(c.quantity); });
    boxes.forEach((b) => b.items.forEach((it) => { r[norm(it.name)] = (r[norm(it.name)] || 0) - num(it.quantity); }));
    return r;
  }, [boxes, items]);
  const unassigned = Object.entries(remaining).filter(([, v]) => v !== 0);
  if (!pf) return null;

  const payload = () => ({ shipper_text: shipper, consignee_text: consignee, notes, boxes: boxes.map((b) => ({ label: b.label, length_cm: num(b.length_cm), width_cm: num(b.width_cm), height_cm: num(b.height_cm), weight_kg: num(b.weight_kg), items: b.items.filter((it) => it.name).map((it) => ({ name: it.name, origin: it.origin || "", batch_number: it.batch_number || "", expiry_date: it.expiry_date || "", quantity: num(it.quantity) })) })) });
  const validate = () => {
    if (!boxes.length) return "Add at least one box";
    if (boxes.some((b) => !num(b.length_cm) || !num(b.width_cm) || !num(b.height_cm) || !num(b.weight_kg))) return "Every box needs dimensions and a gross weight";
    if (!boxes.some((b) => b.items.some((it) => it.name && num(it.quantity) > 0))) return "Add at least one item to a box";
    return null;
  };

  const saveDraft = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/quote-proformas/${pf.id}/packing-list/draft`, payload()); setMeta((m) => ({ ...m, status: data.status, saved_at: data.saved_at })); toast.success("Packing list draft saved"); onChanged?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not save draft"); } finally { setBusy(false); }
  };
  const issue = async () => {
    setConfirm(false); setBusy(true);
    try {
      const { data } = await api.post(`/quote-proformas/${pf.id}/packing-list`, payload(), { responseType: "blob" });
      download(data, `PL-${pf.proforma_number}.pdf`);
      toast.success("Packing list issued and PDF downloaded"); onChanged?.(); onOpenChange(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not issue the packing list"); } finally { setBusy(false); }
  };
  const downloadIssued = async () => {
    try { const { data } = await api.get(`/quote-proformas/${pf.id}/packing-list/pdf`, { responseType: "blob" }); download(data, `PL-${pf.proforma_number}.pdf`); }
    catch { toast.error("No issued packing list to download"); }
  };
  const askIssue = () => { const err = validate(); if (err) return toast.error(err); setConfirm(true); };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-4xl max-h-[92dvh] overflow-y-auto" data-testid="proforma-packing-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 flex-wrap"><ListChecks size={18} className="text-primary" /> Packing list — {pf.proforma_number} <PackingStatusChip status={meta.status} /></DialogTitle>
            {meta.status === "issued" && <p className="text-xs text-muted-foreground" data-testid="ppl-issued-info">Issued {fmtDateTime(meta.issued_at)} by {meta.issued_by}. Editing and saving will return it to draft until re-issued.</p>}
            {meta.status === "draft" && meta.saved_at && <p className="text-xs text-muted-foreground" data-testid="ppl-draft-info">Draft saved {fmtDateTime(meta.saved_at)}.</p>}
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Shipper"><Textarea rows={3} value={shipper} onChange={(e) => setShipper(e.target.value)} className="rounded-sm text-sm" data-testid="ppl-shipper" /></Field>
              <Field label="Consignee"><Textarea rows={3} value={consignee} onChange={(e) => setConsignee(e.target.value)} className="rounded-sm text-sm" data-testid="ppl-consignee" /></Field>
            </div>
            {boxes.map((b, i) => <BoxCard key={i} b={b} i={i} originMode pickMode="select" setBox={(nb) => setBoxes(boxes.map((x, idx) => (idx === i ? nb : x)))} remove={() => setBoxes(boxes.filter((_, idx) => idx !== i))} contents={items} remaining={remaining} />)}
            <Button type="button" variant="outline" className="rounded-sm" onClick={() => setBoxes([...boxes, blankBox()])} data-testid="ppl-add-box"><Plus size={15} className="mr-1.5" /> Add box</Button>
            {unassigned.length > 0 && (
              <div className="text-xs rounded-sm border border-amber-300 bg-amber-50 text-amber-800 px-3 py-2 space-y-0.5" data-testid="ppl-unassigned-warning">
                {unassigned.map(([k, v]) => <p key={k}>{Math.abs(v)} × <span className="font-medium">{k}</span> {v > 0 ? "from the proforma not yet assigned to a box." : "more in boxes than on the proforma."}</p>)}
              </div>
            )}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 rounded-md bg-[#0D1B2A] text-white p-3 text-sm" data-testid="ppl-totals">
              <div><p className="text-[10px] uppercase tracking-wide opacity-70">Boxes</p><p className="font-semibold tabular-nums">{boxes.length}</p></div>
              <div><p className="text-[10px] uppercase tracking-wide opacity-70">Gross weight</p><p className="font-semibold tabular-nums">{totals.weight.toFixed(2)} kg</p></div>
              <div><p className="text-[10px] uppercase tracking-wide opacity-70">Total pcs</p><p className="font-semibold tabular-nums">{totals.qty}</p></div>
              <div><p className="text-[10px] uppercase tracking-wide opacity-70">Volume</p><p className="font-semibold tabular-nums text-[#C9A24A]">{totals.cbm.toFixed(4)} CBM</p></div>
            </div>
            <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Notes (optional)" className="rounded-sm" data-testid="ppl-notes" />
          </div>
          <DialogFooter className="flex-wrap gap-2">
            {meta.status === "issued" && <Button variant="ghost" className="rounded-sm sm:mr-auto" onClick={downloadIssued} data-testid="ppl-download-issued"><DownloadSimple size={16} className="mr-2" /> Download issued PDF</Button>}
            <Button variant="outline" className="rounded-sm" onClick={() => onOpenChange(false)}>Close</Button>
            <Button variant="outline" className="rounded-sm" onClick={saveDraft} disabled={busy} data-testid="ppl-save-draft"><FloppyDisk size={16} className="mr-2" /> Save draft</Button>
            <Button className="rounded-sm" onClick={askIssue} disabled={busy} data-testid="ppl-create"><SealCheck size={16} className="mr-2" /> {busy ? "Working…" : meta.status === "issued" ? "Re-issue final PDF" : "Issue final PDF"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent data-testid="ppl-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Issue final packing list?</AlertDialogTitle>
            <AlertDialogDescription>
              {boxes.length} box(es) · {totals.qty} pcs · {totals.weight.toFixed(2)} kg · {totals.cbm.toFixed(4)} CBM.
              {unassigned.length > 0 && <span className="block mt-2 text-amber-700">Note: some quantities don't match the proforma (see warning). You can still issue.</span>}
              <span className="block mt-2">The PDF will be generated and this list will be marked as issued.</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm" data-testid="ppl-confirm-cancel">Back to editing</AlertDialogCancel>
            <AlertDialogAction className="rounded-sm" onClick={issue} data-testid="ppl-confirm-issue">Issue & download</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
